// ─── S3 Pre-signed Multipart Upload — shared session helper ──────────────────
//
// This is a direct port of poc_s3_multipart.html into a reusable factory.
// One session object is created per recording context (camera, screen share,
// communication video). Sessions are fully independent — they never share
// partNumber, parts, pendingBlob or inflightUpload.
//
// Usage:
//   const session = createMultipartSession();
//   await session.initiate(fileName, bucketPath, pathPrefix, mimeType, extraPayload);
//   // inside ondataavailable:
//   session.feedBlob(event.data);
//   // inside onstop:
//   await session.flush();   // uploads tail, calls complete
//
// Deferred (not implemented here — added in final frontend):
//   - Authentication headers on backend calls (handled by uploadService interceptor)
//   - showRefreshModal() on exhausted retries
//   - Post-processing webhook after complete

import {
	s3InitiateUpload,
	s3PresignPart,
	s3StoreEtag,
	s3CompleteUpload,
	s3AbortUpload,
} from "../apiFunction.js";

// ─── MIME type selection (ported directly from POC) ───────────────────────────
export function pickMimeType() {
	const candidates = [
		"video/webm;codecs=vp9,opus",
		"video/webm;codecs=vp8,opus",
		"video/webm;codecs=h264,opus",
		"video/webm",
		"video/mp4",
	];
	for (const m of candidates) {
		if (MediaRecorder.isTypeSupported(m)) return m;
	}
	return "video/webm";
}

// ─── Retry wrapper for backend API calls (ported directly from POC) ───────────
// Up to 3 attempts, exponential backoff (1 s, 2 s, 4 s).
// Only retries on network errors or 5xx — a 4xx means the request is bad.
async function callWithRetry(apiFn, body, { maxAttempts = 3 } = {}) {
	let delay = 1000;
	for (let attempt = 1; attempt <= maxAttempts; attempt++) {
		try {
			const res = await apiFn(body);
			// axios wraps the response — treat non-2xx as an error
			if (res.status && res.status >= 400) {
				const err = new Error(`API error ${res.status}`);
				err.status = res.status;
				throw err;
			}
			return res.data ? res.data.data ?? res.data : res;
		} catch (err) {
			const is5xx = err.status >= 500 || (err.response && err.response.status >= 500);
			const isNetwork = !err.status && !err.response; // no response at all
			if (attempt === maxAttempts || (!is5xx && !isNetwork)) throw err;
			console.warn(
				`[s3Multipart] attempt ${attempt}/${maxAttempts} failed — retrying in ${delay / 1000}s…`,
				err.message
			);
			await new Promise((res) => setTimeout(res, delay));
			delay *= 2;
		}
	}
}

// ─── Session factory ──────────────────────────────────────────────────────────
export function createMultipartSession() {
	// Internal state — mirrors rec{} in the POC exactly
	const state = {
		fileID:         null,
		uploadId:       null,
		key:            null,
		bucket:         null,
		partNumber:     0,            // last part number SUCCESSFULLY uploaded
		parts:          [],           // [ { PartNumber, ETag }, … ] — kept in sync with Redis
		pendingBlob:    null,         // accumulator between timeslice events
		mimeType:       "video/webm",
		chunkBytes:     5 * 1024 * 1024, // 5 MB minimum part size (S3 requirement)
		// Promise that resolves when the in-flight ondataavailable upload finishes.
		// onstop (flush) awaits this before uploading the final tail.
		inflightUpload: Promise.resolve(),
		uploadFailed:   false,
	};

	// ─── Upload one part directly to S3 (ported from POC uploadPart()) ──────
	async function uploadPart(blob) {
		// Read the next part number but do NOT commit it yet.
		// Only increment after a successful PUT so a failed attempt doesn't
		// create a gap in part numbers (CompleteMultipartUpload requires
		// sequential, contiguous parts).
		const pn = state.partNumber + 1;

		console.log(`[s3Multipart] Part ${pn}: requesting pre-signed URL (${(blob.size / 1024).toFixed(1)} KB)`);
		const presignData = await callWithRetry(s3PresignPart, {
			uploadId:   state.uploadId,
			key:        state.key,
			partNumber: pn,
			...(state.bucket ? { bucket: state.bucket } : {}),
		});
		const { url } = presignData;

		console.log(`[s3Multipart] Part ${pn}: uploading to S3…`);
		// Retry the S3 PUT up to 3 times with exponential backoff (1 s, 2 s, 4 s).
		// Presigned URLs are valid for 1 hour so the same URL is reused across attempts.
		let res;
		{
			let delay = 1000;
			const maxAttempts = 3;
			for (let attempt = 1; attempt <= maxAttempts; attempt++) {
				res = await fetch(url, {
					method:  "PUT",
					body:    blob,
					headers: { "Content-Type": blob.type || "video/webm" },
				});
				if (res.ok) break;
				if (attempt === maxAttempts) {
					throw new Error(
						`S3 PUT part ${pn} failed after ${maxAttempts} attempts: ${res.status}`
					);
				}
				console.warn(
					`[s3Multipart] Part ${pn}: S3 PUT failed (attempt ${attempt}/${maxAttempts}, status ${res.status}) — retrying in ${delay / 1000}s…`
				);
				await new Promise((r) => setTimeout(r, delay));
				delay *= 2;
			}
		}

		// PUT succeeded — now safe to commit the incremented part number
		state.partNumber = pn;

		const etag = res.headers.get("ETag") || res.headers.get("etag");
		if (!etag) {
			throw new Error(
				`No ETag for part ${pn} — add ExposeHeaders: ETag to bucket CORS`
			);
		}

		state.parts.push({ PartNumber: pn, ETag: etag });

		// store-etag: fire-and-forget with retry — Redis is the fallback source
		// of truth for completing stale sessions after a client refresh.
		callWithRetry(s3StoreEtag, {
			fileID:     state.fileID,
			partNumber: pn,
			etag,
		}).catch((err) =>
			console.warn(
				`[s3Multipart] store-etag part ${pn} failed after all retries:`,
				err.message
			)
		);

		console.log(`[s3Multipart] Part ${pn}: done — ETag ${etag}`);
	}

	// ─── Public API ──────────────────────────────────────────────────────────

	// Call once before starting MediaRecorder.
	// extraPayload: any additional fields the backend requires (userID, etc.)
	async function initiate(fileName, bucketPath, pathPrefix, mimeType, extraPayload = {}) {
		state.mimeType = mimeType;

		console.log("[s3Multipart] Initiating upload session…");
		const init = await callWithRetry(s3InitiateUpload, {
			fileName,
			bucketPath,
			pathPrefix,
			contentType: mimeType,
			...extraPayload,
		});

		state.fileID   = init.fileID;
		state.uploadId = init.uploadId;
		state.key      = init.key;
		state.bucket   = init.bucket;
		// Always a fresh session — partNumber starts at 0
		console.log(`[s3Multipart] New session — fileID: ${init.fileID}, key: ${init.key}`);
		console.log("[s3Multipart] (any previous incomplete upload is being finished in the background)");
	}

	// Call from ondataavailable. Accumulates data into pendingBlob and
	// triggers an upload whenever the accumulated size meets chunkBytes.
	// Mirrors the POC ondataavailable handler exactly.
	function feedBlob(blob) {
		if (!blob || blob.size === 0) return;

		// Accumulate into pendingBlob
		state.pendingBlob = state.pendingBlob
			? new Blob([state.pendingBlob, blob], { type: state.mimeType })
			: blob;

		// Upload once we've accumulated a full chunk.
		// The final-chunk flush is handled entirely in flush() (onstop).
		// We must NOT flush here when the recorder has already stopped because
		// onstop fires synchronously before this async callback can run, and
		// complete() would be called before the PUT finishes.
		if (state.pendingBlob.size >= state.chunkBytes) {
			const toUpload    = state.pendingBlob;
			state.pendingBlob = null;
			// Chain uploads so they stay sequential and flush() can await the tail
			state.inflightUpload = state.inflightUpload
				.then(() => uploadPart(toUpload))
				.catch((err) => {
					console.error("[s3Multipart] Part upload error:", err.message);
					state.uploadFailed = true;
				});
		}
	}

	// Call from onstop. Awaits any in-flight upload, flushes the tail blob,
	// then calls complete (or abort on failure). Mirrors the POC onstop exactly.
	async function flush(onComplete, onError) {
		// Wait for any in-progress chunk upload to finish first
		await state.inflightUpload;

		// Flush whatever is still in the accumulator (the final sub-5MB tail)
		if (state.pendingBlob && state.pendingBlob.size > 0) {
			const tail        = state.pendingBlob;
			state.pendingBlob = null;
			try {
				await uploadPart(tail);
			} catch (err) {
				console.error("[s3Multipart] Final chunk upload error:", err.message);
				state.uploadFailed = true;
			}
		}

		if (state.uploadFailed) {
			console.error("[s3Multipart] A part upload failed after all retries — aborting.");
			await abortSession();
			if (onError) onError(new Error("Part upload failed after all retries"));
			return;
		}

		if (state.parts.length === 0) {
			console.error("[s3Multipart] No data recorded — aborting.");
			await abortSession();
			if (onError) onError(new Error("No data recorded"));
			return;
		}

		console.log(`[s3Multipart] All parts uploaded (${state.parts.length} total). Completing…`);

		try {
			const result = await callWithRetry(s3CompleteUpload, {
				fileID:   state.fileID,
				uploadId: state.uploadId,
				key:      state.key,
				parts:    state.parts, // fallback in case Redis TTL expired
				...(state.bucket ? { bucket: state.bucket } : {}),
			});
			console.log(`[s3Multipart] Done! Bucket: ${result.bucket} | Key: ${result.key}`);
			if (onComplete) onComplete(result);
		} catch (err) {
			console.error("[s3Multipart] Complete failed:", err.message);
			if (onError) onError(err);
		}
	}

	async function abortSession() {
		if (!state.uploadId) return;
		try {
			await s3AbortUpload({
				fileID:   state.fileID,
				uploadId: state.uploadId,
				key:      state.key,
				...(state.bucket ? { bucket: state.bucket } : {}),
			});
		} catch (err) {
			// abort is best-effort — swallow errors
			console.warn("[s3Multipart] Abort failed (best-effort):", err.message);
		}
	}

	return { initiate, feedBlob, flush, abort: abortSession };
}
